import { Button, InputAdornment } from "@mui/material";
import React, { useState } from "react";
import {
  ModalButtonContainer,
  NegativeModalButton,
  PositiveModalButton,
} from "../../components/Button";
import { useModal } from "../../components/modal";
import { CustomTextField } from "../../components/TextFields";
import ImageUploader from "../../components/uploader/imageUploader";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { Row } from "../../global/styledComponents";
import { SelectInput } from "../../components/inputs/SelectInput";
import { User } from "../../types/user";
import { AccountState } from "../../types/accountState";
import PersonIcon from "@mui/icons-material/Person";
import {
  useAddUserMutation,
  useUpdateUserMutation,
} from "../../app/services/usres.api";
import objToFromData from "../../utils/objToFromdata";
import LoadingButton from "../../components/Button/LoadingButton";

const schema = yup.object().shape({
  __t: yup
    .string()
    .matches(/User|Organ/)
    .required()
    .default("User"),
  //@ts-ignore
  name: yup.string().when(["__t"], {
    is: "Organ",
    then: (schema) => schema.required("نام سازمان الزامی است"),
  }),
  //@ts-ignore
  firstName: yup.string().when(["__t"], {
    is: "User",
    then: (schema) => schema.required("نام سازمان الزامی است"),
  }),
  //@ts-ignore
  lastName: yup.string().when(["__t"], {
    is: "User",
    then: (schema) => schema.required("نام سازمان الزامی است"),
  }),
  //@ts-ignore
  teacher: yup.boolean().when(["__t"], {
    is: "User",
    then: (schema) => schema.required(""),
  }),
  phone: yup.string().required("شماره تماس الزامی است."),
  //@ts-ignore
  commision: yup.number().when(["__t", "teacher"], {
    is: (__t: string, teacher: boolean) => __t === "User" && teacher === true,
    then: (schema) => schema.required(),
  }),
  accountState: yup.string(),
  desc: yup.string(),
});

export default function AddModal({
  defaultValue,
}: {
  defaultValue?: Omit<User, "isAdmin">;
}) {
  const {
    setValue,
    getValues,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValue
      ? defaultValue
      : {
          __t: "User",
          teacher: false,
        },
  });
  const [profile, setProfile] = useState<File | null>(null);
  const [addUser, addUserState] = useAddUserMutation();
  const [updateUser, updateUserState] = useUpdateUserMutation();
  const isOrgan = watch("__t") === "Organ";
  const isTeacher = watch("teacher") ?? false;
  const isEdit = defaultValue?.__t ? true : false;
  const { setShow } = useModal();
  return (
    <React.Fragment>
      <SelectInput
        required
        error={!!errors.teacher}
        helperText={errors.teacher?.message}
        defaultValue={{
          label: getValues("__t") === "Organ" ? "سازمان" : "عادی",
        }}
        onChange={(value: { label: string } | null) => {
          if (value?.label === "سازمان") setValue("__t", "Organ");
          else setValue("__t", "User");
        }}
        label="نوع حساب"
        options={[{ label: "سازمان" }, { label: "عادی" }]}
      />
      {isOrgan ? (
        <CustomTextField
          required
          label="نام"
          placeholder="نام را وارد کنید."
          variant="outlined"
          defaultValue={getValues("name")}
          onChange={(e) => setValue("name", e.target.value)}
          error={!!errors.name}
          helperText={errors.name?.message}
        />
      ) : (
        <Row>
          <CustomTextField
            required
            label="firstName"
            placeholder="نام را وارد کنید."
            variant="outlined"
            defaultValue={getValues("firstName")}
            onChange={(e) => setValue("firstName", e.target.value)}
            error={!!errors.firstName}
            helperText={errors.firstName?.message}
          />
          ,
          <CustomTextField
            required
            label="lastName"
            placeholder="نام را وارد کنید."
            defaultValue={getValues("lastName")}
            variant="outlined"
            onChange={(e) => setValue("lastName", e.target.value)}
            error={!!errors.lastName}
            helperText={errors.lastName?.message}
          />
        </Row>
      )}
      <ImageUploader
        title="تصویر"
        onChange={(file) => setProfile(file)}
        required
      />

      <CustomTextField
        label="شماره همراه"
        onChange={(e) => setValue("phone", e.target.value)}
        error={!!errors.phone}
        helperText={errors.phone?.message}
        required
        variant="outlined"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <PersonIcon sx={{ color: "divider", padding: "5px" }} />
            </InputAdornment>
          ),
        }}
      />
      {!isOrgan && (
        <SelectInput
          required
          error={!!errors.teacher}
          helperText={errors.teacher?.message}
          defaultValue={{ label: getValues("teacher") ? "استاد" : "کاربر" }}
          onChange={(e) => {
            if (e?.label === "کاربر") setValue("teacher", false);
            else setValue("teacher", true);
          }}
          label="نوع کاربری"
          options={[{ label: "کاربر" }, { label: "استاد" }]}
        />
      )}
      {isTeacher && (
        <CustomTextField
          required
          label="کمیسیون"
          placeholder="کمیسیون را وارد کنید."
          variant="outlined"
          defaultValue={getValues("commision")}
          type="number"
          onChange={(e) => setValue("commision", Number(e.target.value))}
          error={!!errors.commision}
          helperText={errors.commision?.message}
        />
      )}
      <CustomTextField
        label="توضیحات"
        placeholder="توضیحات را وارد کنید."
        variant="outlined"
        defaultValue={getValues("desc")}
        multiline
        minRows={2}
        maxRows={4}
        onChange={(e) => setValue("desc", e.target.value)}
        error={!!errors.desc}
        helperText={errors.desc?.message}
      />
      {isEdit && (
        <SelectInput
          required
          error={!!errors.teacher}
          helperText={errors.teacher?.message}
          onChange={(e) => {
            setValue("accountState", e?.value);
          }}
          defaultValue={{
            label:
              getValues("accountState") === AccountState.ACTIVE
                ? "فعال"
                : "غیرفعال",
            value: getValues("accountState"),
          }}
          label="نوع حساب"
          options={[
            { label: "فعال", value: AccountState.ACTIVE },
            { label: "غیرفعال", value: AccountState.BAN },
          ]}
        />
      )}
      <ModalButtonContainer>
        <Button
          style={NegativeModalButton}
          sx={{
            color: "divider",
            bgcolor: "primary.main",
            borderStyle: "solid",
            borderWidth: "1px",
            borderColor: "divider",
            ":hover": {
              bgcolor: "divider",
              color: "primary.main",
            },
          }}
          onClick={() => setShow(false)}
        >
          انصراف
        </Button>
        <LoadingButton
          style={PositiveModalButton}
          sx={{
            ":hover": {
              bgcolor: "customColors.purple",
              color: "secondary.main",
            },
          }}
          isLoading={addUserState.isLoading || updateUserState.isLoading}
          onClick={handleSubmit((data) => {
            const formData = objToFromData(data);
            if (profile) formData.append("profile", profile);

            if (isEdit && defaultValue) {
              return updateUser({ userId: defaultValue._id, data: formData })
                .unwrap()
                .then(() => setShow(false))
                .catch((err) => {});
            }

            addUser(formData)
              .unwrap()
              .then(() => setShow(false))
              .catch((err) => {});
          })}
        >
          تایید
        </LoadingButton>
      </ModalButtonContainer>
    </React.Fragment>
  );
}
